<template>
  <div class="ca-page bg-light-page">

    <!-- Header -->
    <div class="ca-header px-3">
      <button class="ca-back-btn" @click="$router.back()">
        <arrow-left-icon :size="20" />
      </button>
      <span class="ca-header-title text-18 font-bold">Activity</span>
    </div>

    <!-- Tab switcher -->
    <div class="ca-tab-bar px-3">
      <button
        class="ca-tab text-13 font-semibold"
        :class="{ 'ca-tab--active': activeTab === 'orders' }"
        @click="setTab('orders')"
      >Orders</button>
      <button
        class="ca-tab text-13 font-semibold"
        :class="{ 'ca-tab--active': activeTab === 'requests' }"
        @click="setTab('requests')"
      >Requests</button>
    </div>

    <!-- Status chips -->
    <div class="ca-chips-row px-3">
      <button
        v-for="chip in statusChips"
        :key="chip.id"
        class="ca-chip text-11 font-semibold"
        :class="{ 'ca-chip--active': statusFilter === chip.id }"
        @click="setStatus(chip.id)"
      >{{ chip.label }}</button>
    </div>

    <!-- Scrollable list area -->
    <div class="ca-scroll" ref="scrollContainer">

      <!-- Loading state -->
      <div v-if="isLoading" class="px-3 pt-2">
        <div v-for="i in 4" :key="i" class="ca-skeleton-card" />
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="ca-state-box px-3 pt-4 text-center">
        <p class="text-13" style="color:#6B7886;margin-bottom:12px">{{ error }}</p>
        <button class="ca-retry-btn text-13 font-semibold" @click="fetchHistory(true)">Retry</button>
      </div>

      <!-- Empty state -->
      <div v-else-if="txList.length === 0" class="ca-state-box px-3 pt-4 text-center">
        <p class="text-13 font-semibold" style="color:#1C2B36;margin-bottom:4px">No activity found</p>
        <p class="text-12" style="color:#6B7886">Your orders and requests will appear here</p>
      </div>

      <!-- List -->
      <div v-else class="ca-list px-3">
        <div
          v-for="item in enrichedTxList"
          :key="item.id"
          class="ca-card"
          :style="{ '--ca-accent': item.iconMeta.color }"
          @click="openDetail(item)"
        >
          <div class="ca-icon-box" :style="{ background: item.iconMeta.bg, color: item.iconMeta.color }">
            <component :is="item.iconMeta.icon" :size="20" />
          </div>
          <div class="ca-card-body">
            <div class="ca-card-top">
              <span class="ca-card-title text-13 font-semibold">{{ getTitle(item) }}</span>
              <span class="ca-card-date text-11">{{ getMeta(item) }}</span>
            </div>
            <div v-if="item.tx_type === 'ORDER' && item.cardSubtitle" class="ca-card-subtitle text-11">
              {{ item.cardSubtitle }}
            </div>
            <div class="ca-card-bottom">
              <span class="ca-status-badge" :style="badgeSurface(item.status)">
                <span class="ca-status-dot" :style="dotStyle(item.status)" />
                <span class="text-11 font-semibold" :style="{ color: badgeColor(item.status) }">{{ statusLabel(item.status) }}</span>
              </span>
              <span v-if="item.tx_type === 'ORDER' && item.invoice" class="ca-card-invoice text-10">
                {{ '#' + item.invoice.split('/').pop() }}
              </span>
              <span v-if="getAmount(item)" class="ca-card-amount text-12 font-semibold">
                {{ getAmount(item) }}
              </span>
            </div>
          </div>
          <chevron-right-icon :size="15" class="ca-chevron" />
        </div>
      </div>

      <!-- Load more spinner -->
      <div v-if="isLoadingMore" class="ca-load-more py-3 text-center">
        <div class="ca-spinner" />
      </div>

      <div style="height:80px" />
    </div>

    <!-- Bottom nav -->
    <ConciergeBottomNav />

    <!-- Detail sheet -->
    <ConciergeActivityDetailSheet
      v-if="selectedItem"
      :item="selectedItem"
      @close="selectedItem = null"
    />

  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import {
    ArrowLeft as ArrowLeftIcon,
    ChevronRight as ChevronRightIcon,
    Coffee, Compass, Home, AlertTriangle, Heart, Star, Ticket,
  } from 'lucide-vue'
  import { get } from '@/services/index.js'
  import { formatCurrency, customFormatDateTime } from '@/utils/index.js'
  import ConciergeBottomNav           from '@/components/v2/ConciergeBottomNav'
  import ConciergeActivityDetailSheet from '@/components/v2/ConciergeActivityDetailSheet'

  const STATUS_CHIPS = [
    { id: 'ALL',             label: 'All' },
    { id: 'PENDING',         label: 'Pending' },
    { id: 'ONPROGRESS',      label: 'On Progress' },
    { id: 'WAITING_PAYMENT', label: 'Waiting Payment' },
    { id: 'COMPLETE',        label: 'Completed' },
    { id: 'CANCELLED',       label: 'Cancelled' },
  ]

  const MODULE_ICON_MAP = {
    housekeeping:    { bg: '#EAF4EC', color: '#3A8A48',  icon: Home },
    'report-issue':  { bg: '#FEE9E4', color: '#C04A30',  icon: AlertTriangle },
    dining:          { bg: '#FEE9E4', color: '#C04A30',  icon: Coffee },
    'room-dining':   { bg: '#FEE9E4', color: '#C04A30',  icon: Coffee },
    activity:        { bg: '#FEF3E2', color: '#C07B20',  icon: Compass },
    activities:      { bg: '#FEF3E2', color: '#C07B20',  icon: Compass },
    spa:             { bg: '#EAF4F1', color: '#2A7A60',  icon: Compass },
    voucher:         { bg: '#F0EBFE', color: '#8B5CF6',  icon: Ticket },
    tip:             { bg: '#FCE8EF', color: '#C03060',  icon: Heart },
    feedback:        { bg: '#E6F0FB', color: '#2B6FB8',  icon: Star },
    default:         { bg: '#FEE9E4', color: '#C04A30',  icon: Coffee },
  }

  const STATUS_STYLE = {
    PENDING:            { background: '#FFF8E6', color: '#B87800' },
    ONPROGRESS:         { background: '#E6EFF4', color: 'var(--merchant-primary-color)' },
    COMPLETE:           { background: '#E6F9EE', color: '#1A7A45' },
    CANCELLED:          { background: '#F3F3F5', color: '#8A929A' },
    WAITING_PAYMENT:    { background: '#FFF4E6', color: '#E07B27' },
    COMPLETE_CANCELLED: { background: '#F3F3F5', color: '#8A929A' },
    PENDING_ONPROGRESS: { background: '#E6EFF4', color: 'var(--merchant-primary-color)' },
  }

  const STATUS_LABEL = {
    PENDING:            'Pending',
    ONPROGRESS:         'On Progress',
    COMPLETE:           'Completed',
    CANCELLED:          'Cancelled',
    WAITING_PAYMENT:    'Waiting Payment',
    COMPLETE_CANCELLED: 'Cancelled',
    PENDING_ONPROGRESS: 'On Progress',
  }

  export default {
    name: 'ConciergeHistory',

    components: {
      ArrowLeftIcon,
      ChevronRightIcon,
      ConciergeBottomNav,
      ConciergeActivityDetailSheet,
    },

    metaInfo() {
      return {
        title: 'Activity',
        titleTemplate: this.getMetaTitleTemplate,
      }
    },

    data() {
      return {
        txList: [],
        isLoading: false,
        isLoadingMore: false,
        error: '',
        page: 1,
        lastPage: 1,
        hasMore: true,
        activeTab: 'orders',
        statusFilter: 'ALL',
        selectedItem: null,
      }
    },

    computed: {
      ...mapGetters(['getMetaTitleTemplate']),

      currentTxType() {
        return this.activeTab === 'orders' ? 'ORDER' : 'SERVICE'
      },

      statusChips() {
        return STATUS_CHIPS
      },

      filterKey() {
        return this.activeTab + ':' + this.statusFilter
      },

      enrichedTxList() {
        return this.txList.map(function(item) {
          var slug = item.module && item.module.slug
          var iconMeta = MODULE_ICON_MAP[slug] || MODULE_ICON_MAP.default
          var cardSubtitle = null
          if (item.tx_type === 'ORDER' && Array.isArray(item.tx_detail_list) && item.tx_detail_list.length) {
            var first = item.tx_detail_list[0].product_name
            var extra = item.tx_detail_list.length - 1
            cardSubtitle = extra > 0 ? first + ' +' + extra + ' more' : first
          }
          return Object.assign({}, item, { iconMeta: iconMeta, cardSubtitle: cardSubtitle })
        })
      },
    },

    watch: {
      filterKey() {
        this.fetchHistory(true)
      },
    },

    mounted() {
      this.$refs.scrollContainer.addEventListener('scroll', this.handleScroll)
      this.fetchHistory(true)
    },

    beforeDestroy() {
      this.$refs.scrollContainer.removeEventListener('scroll', this.handleScroll)
    },

    methods: {
      ...mapGetters(['getMerchant', 'getConciergeHomeAuth']),

      fetchHistory(reset) {
        if (reset) {
          this.txList = []
          this.page = 1
          this.hasMore = true
          this.error = ''
        }
        if (!this.hasMore) return

        const isFirstPage = this.page === 1
        if (isFirstPage) {
          this.isLoading = true
        } else {
          this.isLoadingMore = true
        }

        const auth = this.getConciergeHomeAuth()
        const params = {
          pms_detail_id: auth.pms_detail_id,
          folio_no: auth.folio_number,
          merchant_id: this.getMerchant().id,
          status: this.statusFilter,
          tx_type: this.currentTxType,
          order_type: 'DESC',
          datetime_type: 'NO_USE_FILTER_DATE',
          page: this.page,
          page_size: 10,
        }

        get('CONCIERGE', 'tx-history', params, { type:'concierge_token_direct', token: auth.token})
          .then((response) => {
            const res = response.data
            if (!res.success) {
              this.error = res.message
              return
            }
            this.txList = this.txList.concat(res.data)
            this.lastPage = res.last_page
            this.hasMore = this.page < res.last_page
          })
          .catch(() => {
            this.error = 'Failed to load history.'
          })
          .finally(() => {
            this.isLoading = false
            this.isLoadingMore = false
          })
      },

      loadMore() {
        if (this.isLoadingMore || this.isLoading || !this.hasMore) return
        this.page += 1
        this.fetchHistory(false)
      },

      handleScroll() {
        const el = this.$refs.scrollContainer
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
          this.loadMore()
        }
      },

      setTab(tab) {
        this.activeTab = tab
        this.statusFilter = 'ALL'
      },

      setStatus(id) {
        this.statusFilter = id
      },

      badgeSurface(status) {
        return { background: (STATUS_STYLE[status] || STATUS_STYLE.PENDING).background }
      },

      badgeColor(status) {
        return (STATUS_STYLE[status] || STATUS_STYLE.PENDING).color
      },

      dotStyle(status) {
        return { background: (STATUS_STYLE[status] || STATUS_STYLE.PENDING).color }
      },

      statusLabel(status) {
        return STATUS_LABEL[status] || status
      },

      getTitle(item) {
        if (item.tx_type === 'ORDER') {
          return (item.module && item.module.name) || 'Order'
        }
        return (item.module && item.module.name) || 'Service'
      },

      getMeta(item) {
        return customFormatDateTime(item.created_at, 'null', 'DD MMM · HH:mm')
      },

      getAmount(item) {
        const val = parseFloat(item.total_payment)
        if (!val || val === 0) return null
        return formatCurrency(val, true)
      },

      openDetail(item) {
        this.selectedItem = item
      },
    },
  }
</script>

<style scoped>
.ca-page {
  height: 100vh;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
}

.ca-scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

/* Header */
.ca-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 16px;
  padding-bottom: 16px;
  background: var(--bg-light-page, #F3F6F9);
}

.ca-back-btn {
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #E0E8EF;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #1C2B36;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}

.ca-header-title {
  color: #1C2B36;
  letter-spacing: -0.2px;
}

/* Tab bar */
.ca-tab-bar {
  display: flex;
  gap: 8px;
  background: var(--bg-light-page, #F3F6F9);
  padding-top: 2px;
  padding-bottom: 10px;
}

.ca-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: 999px;
  border: 1.5px solid #E0E8EF;
  background: #fff;
  color: #6B7886;
  cursor: pointer;
  transition: all .15s;
}

.ca-tab--active {
  background: #1C2B36;
  color: #fff;
  border-color: #1C2B36;
}

/* Chips */
.ca-chips-row {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 14px;
  background: var(--bg-light-page, #F3F6F9);
  scrollbar-width: none;
}

.ca-chips-row::-webkit-scrollbar {
  display: none;
}

.ca-chip {
  flex-shrink: 0;
  padding: 5px 13px;
  border-radius: 999px;
  border: 1.5px solid #E0E8EF;
  background: #fff;
  color: #6B7886;
  cursor: pointer;
  white-space: nowrap;
  transition: all .15s;
}

.ca-chip--active {
  background: var(--merchant-primary-color, #2B5F7A);
  color: #fff;
  border-color: var(--merchant-primary-color, #2B5F7A);
}

/* Skeleton */
.ca-skeleton-card {
  height: 80px;
  border-radius: 16px;
  background: linear-gradient(90deg, #E8EEF3 25%, #F3F6F9 50%, #E8EEF3 75%);
  background-size: 200% 100%;
  animation: ca-shimmer 1.4s infinite;
  margin-bottom: 10px;
}

@keyframes ca-shimmer {
  0%   { background-position: 200% 0 }
  100% { background-position: -200% 0 }
}

/* States */
.ca-state-box {
  padding-top: 40px;
}

.ca-retry-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 20px;
  border-radius: 999px;
  border: 1.5px solid #E0E8EF;
  background: #fff;
  color: #1C2B36;
  cursor: pointer;
}

/* List */
.ca-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Card */
.ca-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05), 0 4px 14px rgba(0,0,0,0.07);
  border: 1px solid #EAEFF4;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 12px 14px 16px;
  cursor: pointer;
  transition: transform .12s, background .12s;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  overflow: hidden;
}

.ca-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3.5px;
  background: var(--ca-accent, #E07B27);
  border-radius: 14px 0 0 14px;
}

.ca-card:active {
  background: #F8FAFB;
  transform: scale(0.985);
}

.ca-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ca-card-body {
  flex: 1;
  min-width: 0;
}

.ca-card-top {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 7px;
}

.ca-card-title {
  flex: 1;
  color: #1C2B36;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ca-card-date {
  color: #9AAAB8;
  flex-shrink: 0;
  white-space: nowrap;
  line-height: 1.6;
}

.ca-card-bottom {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ca-card-amount {
  color: #1C2B36;
  margin-left: auto;
}

.ca-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 9px 4px 7px;
  border-radius: 7px;
  white-space: nowrap;
  flex-shrink: 0;
}

.ca-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.ca-chevron {
  color: #C8D3DC;
  flex-shrink: 0;
}

/* Load more */
.ca-load-more {
  display: flex;
  justify-content: center;
  align-items: center;
}

.ca-spinner {
  width: 22px;
  height: 22px;
  border: 2px solid #E0E8EF;
  border-top-color: var(--merchant-primary-color, #2B5F7A);
  border-radius: 50%;
  animation: ca-spin .7s linear infinite;
}

@keyframes ca-spin {
  to { transform: rotate(360deg) }
}

.ca-card-subtitle {
  color: #6B7886;
  margin-top: 1px;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ca-card-invoice {
  color: #9AAAB8;
  background: #F3F6F9;
  border-radius: 5px;
  padding: 2px 6px;
  font-family: Nunito-SemiBold;
  letter-spacing: 0.3px;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/pages/hotel/concierge/ConciergeHistory.vue